// src/pages/AdminAffiliatePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient.js";

import DashboardSidebarAdmin from "../components/Dashboard/DashboardSidebarAdmin.jsx";
import DashboardNavbarAdmin from "../components/Dashboard/DashboardNavbarAdmin.jsx";

const formatRupiah = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const AdminAffiliatePage = () => {
  const navigate = useNavigate();

  // Auth / admin
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Affiliator & voucher
  const [affiliators, setAffiliators] = useState([]);
  const [loadingAffiliators, setLoadingAffiliators] = useState(true);
  const [search, setSearch] = useState("");
  const [vouchersMap, setVouchersMap] = useState({}); // { affiliator_id: voucherRow }
  const [pendingMap, setPendingMap] = useState({}); // { affiliator_id: { count: number, oldest: date } }

  // Riwayat komisi & penarikan
  const [selectedHistoryAff, setSelectedHistoryAff] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [updatingWithdrawalId, setUpdatingWithdrawalId] = useState(null);

  // Local state untuk menyimpan note yang sedang diketik admin (sebelum save)
  const [withdrawalNotes, setWithdrawalNotes] = useState({});

  // 1) Cek auth & role admin
  useEffect(() => {
    const fetchAuth = async () => {
      try {
        setAuthLoading(true);
        const { data, error: authError } = await supabase.auth.getUser();
        const authUser = data?.user;

        if (authError || !authUser) {
          navigate("/login");
          return;
        }
        setUser(authUser);

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", authUser.id)
          .single();

        if (profileError && profileError.code !== "PGRST116") {
          throw profileError;
        }

        if (!profileData || !profileData.phone) {
          navigate("/login");
          return;
        }

        if (profileData.role !== "admin") {
          navigate("/dashboard");
          return;
        }

        setProfile(profileData);
      } catch (err) {
        console.error("Error fetching admin data:", err);
      } finally {
        setAuthLoading(false);
      }
    };

    fetchAuth();
  }, [navigate]);

  // 2) Ambil affiliator + voucher mereka
  useEffect(() => {
    const fetchAffiliators = async () => {
      try {
        setLoadingAffiliators(true);

        // Ambil affiliator_id dari affiliate_commissions
        const { data: commissionRows, error } = await supabase
          .from("affiliate_commissions")
          .select("affiliator_id")
          .not("affiliator_id", "is", null);

        if (error) throw error;

        const uniqueIds = [
          ...new Set(commissionRows.map((row) => row.affiliator_id)),
        ];

        if (uniqueIds.length === 0) {
          setAffiliators([]);
          setVouchersMap({});
          return;
        }

        // Ambil pending withdrawals untuk tahu siapa yang pending & urutan
        const { data: pendingRows, error: pendingError } = await supabase
          .from("affiliate_withdrawals")
          .select("affiliator_id, created_at")
          .eq("status", "pending");

        if (pendingError) throw pendingError;

        const pMap = {};
        const pendingUserIds = new Set();

        (pendingRows || []).forEach((row) => {
          pendingUserIds.add(row.affiliator_id);

          if (!pMap[row.affiliator_id]) {
            pMap[row.affiliator_id] = { count: 0, oldest: row.created_at };
          }
          pMap[row.affiliator_id].count += 1;
          // track oldest
          if (new Date(row.created_at) < new Date(pMap[row.affiliator_id].oldest)) {
            pMap[row.affiliator_id].oldest = row.created_at;
          }
        });
        setPendingMap(pMap);

        // Gabung uniqueIds dari commissions + pendingUserIds (bisa jadi user belum ada komisi tapi sudah ada withdrawal pending? unlikely but safe)
        const allUserIds = [...new Set([...uniqueIds, ...pendingUserIds])];

        if (allUserIds.length === 0) {
          setAffiliators([]);
          setVouchersMap({});
          return;
        }

        // Ambil profil affiliator
        const { data: profilesRows, error: profilesError } = await supabase
          .from("profiles")
          .select("id, username, email, wallet_balance, created_at")
          .in("id", allUserIds);

        if (profilesError) throw profilesError;

        // Custom sort:
        // 1. Yang punya pending (count > 0) -> paling atas
        // 2. Di antara yang pending -> yang "oldest" nya paling lama (ascending date) -> paling atas
        // 3. Sisanya sort by created_at desc (member baru di atas)
        const sorted = [...profilesRows].sort((a, b) => {
          const aPending = pMap[a.id];
          const bPending = pMap[b.id];

          // Jika salah satu punya pending
          if (aPending && !bPending) return -1;
          if (!aPending && bPending) return 1;

          // Jika keduanya punya pending, bandingkan oldest date
          if (aPending && bPending) {
            return new Date(aPending.oldest).getTime() - new Date(bPending.oldest).getTime();
          }

          // Jika tidak ada pending, sort by created_at desc
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        setAffiliators(sorted);

        // Ambil voucher affiliator
        const { data: vouchersRows, error: vouchersError } = await supabase
          .from("vouchers")
          .select("id, code, affiliator_id, commission_percent, is_active")
          .in("affiliator_id", allUserIds);

        if (vouchersError) throw vouchersError;

        const map = {};
        (vouchersRows || []).forEach((v) => {
          if (v.affiliator_id) {
            map[v.affiliator_id] = v;
          }
        });
        setVouchersMap(map);
      } catch (err) {
        console.error("Error fetching affiliators/vouchers:", err);
      } finally {
        setLoadingAffiliators(false);
      }
    };

    if (!authLoading) {
      fetchAffiliators();
    }
  }, [authLoading]);

  // 3) Lihat riwayat komisi & penarikan (card bawah)
  const handleViewHistory = async (aff) => {
    setSelectedHistoryAff(aff);
    setLoadingDetails(true);
    setCommissions([]);
    setWithdrawals([]);

    try {
      const { data: commissionData, error: commissionError } = await supabase
        .from("affiliate_commissions")
        .select(
          "id, buyer_name, voucher_code, transaction_amount, discount_amount, commission_amount, status, created_at"
        )
        .eq("affiliator_id", aff.id)
        .order("created_at", { ascending: false });

      if (commissionError) throw commissionError;

      const { data: withdrawalData, error: withdrawalError } = await supabase
        .from("affiliate_withdrawals")
        .select("id, amount, status, note, created_at, processed_at, bank_name, account_number, account_holder")
        .eq("affiliator_id", aff.id)
        .order("created_at", { ascending: false });

      if (withdrawalError) throw withdrawalError;

      setCommissions(commissionData || []);
      setWithdrawals(withdrawalData || []);
    } catch (err) {
      console.error("Error fetching affiliate history:", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // 4) Ubah status penarikan (pending -> success/failed) + refund saldo kalau gagal
  const handleUpdateWithdrawalStatus = async (withdrawal, newStatus) => {
    if (!selectedHistoryAff) return;

    if (withdrawal.status !== "pending") {
      alert("Hanya penarikan dengan status pending yang bisa diubah.");
      return;
    }

    if (!["success", "failed", "pending"].includes(newStatus)) return;

    const pretty =
      newStatus === "success"
        ? "BERHASIL"
        : newStatus === "failed"
          ? "GAGAL / DIBATALKAN"
          : "PENDING";

    const confirmText =
      newStatus === "success"
        ? "Tandai penarikan ini SUKSES? Saldo tidak akan dikembalikan."
        : newStatus === "failed"
          ? "Batalkan penarikan ini dan kembalikan saldo ke wallet affiliator?"
          : "Ubah kembali ke status PENDING?";

    if (!window.confirm(confirmText)) return;

    // Ambil note dari state lokal (input text area) atau pakai note lama
    const note = withdrawalNotes[withdrawal.id] !== undefined ? withdrawalNotes[withdrawal.id] : (withdrawal.note || "");
    const finalNote = note.trim() || null;

    try {
      setUpdatingWithdrawalId(withdrawal.id);

      // Jika dibatalkan -> kembalikan saldo ke wallet affiliator
      if (newStatus === "failed") {
        // Catat ke revenue_ledger
        await supabase.from("revenue_ledger").insert({
          transaction_id: null,
          wallet_owner_id: selectedHistoryAff.id,
          amount: withdrawal.amount,
          description: `Pengembalian saldo penarikan affiliate #${withdrawal.id}`,
        });

        // Tambah saldo wallet
        const { error: rpcErr } = await supabase.rpc(
          "increment_wallet_balance",
          {
            p_user_id: selectedHistoryAff.id,
            p_delta: withdrawal.amount,
          }
        );

        if (rpcErr) {
          console.error("increment_wallet_balance gagal:", rpcErr);
          alert(
            "Gagal mengembalikan saldo ke wallet. Status tidak diubah. Cek console."
          );
          setUpdatingWithdrawalId(null);
          return;
        }
      }

      const updates = {
        status: newStatus,
        note,
        processed_at:
          newStatus === "pending" ? null : new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("affiliate_withdrawals")
        .update(updates)
        .eq("id", withdrawal.id)
        .select()
        .single();

      if (error) throw error;

      // Update list penarikan di state
      setWithdrawals((prev) =>
        prev.map((w) => (w.id === withdrawal.id ? data : w))
      );

      // Sinkronkan angka wallet di UI kalau saldo dikembalikan
      if (newStatus === "failed") {
        const delta = Number(withdrawal.amount || 0);

        setSelectedHistoryAff((prev) =>
          prev
            ? {
              ...prev,
              wallet_balance:
                Number(prev.wallet_balance || 0) + delta,
            }
            : prev
        );

        setAffiliators((prev) =>
          prev.map((a) =>
            a.id === selectedHistoryAff.id
              ? {
                ...a,
                wallet_balance:
                  Number(a.wallet_balance || 0) + delta,
              }
              : a
          )
        );
      }
    } catch (err) {
      console.error("Error updating withdrawal status:", err);
      alert("Gagal mengubah status penarikan. Cek console.");
    } finally {
      setUpdatingWithdrawalId(null);
    }
  };

  // Ringkasan riwayat
  const totalCommission = useMemo(
    () =>
      commissions.reduce(
        (sum, c) => sum + Number(c.commission_amount || 0),
        0
      ),
    [commissions]
  );

  const totalWithdrawal = useMemo(
    () =>
      withdrawals
        .filter((w) => w.status === "success")
        .reduce((sum, w) => sum + Number(w.amount || 0), 0),
    [withdrawals]
  );

  // Pending count & total (buat di header Riwayat Penarikan)
  const pendingWithdrawalCount = useMemo(
    () => withdrawals.filter((w) => w.status === "pending").length,
    [withdrawals]
  );

  const pendingWithdrawalTotal = useMemo(
    () =>
      withdrawals
        .filter((w) => w.status === "pending")
        .reduce((sum, w) => sum + Number(w.amount || 0), 0),
    [withdrawals]
  );

  // Filter affiliator
  const filteredAffiliators = useMemo(() => {
    if (!search.trim()) return affiliators;
    const q = search.toLowerCase();
    return affiliators.filter((a) => {
      const base = `${a.username || ""} ${a.email || ""}`.toLowerCase();
      return base.includes(q);
    });
  }, [affiliators, search]);

  const selectedHistoryVoucher = selectedHistoryAff
    ? vouchersMap[selectedHistoryAff.id] || null
    : null;

  if (authLoading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-sky-600 border-t-transparent rounded-full mb-3" />
        <p className="text-slate-500 text-sm">Memuat admin dashboard...</p>
      </div>
    );
  }

  const displayName =
    profile?.username || user?.email?.split("@")[0] || "Admin";

  return (
    <div className="h-screen overflow-hidden bg-slate-50 font-sans">
      {/* flex utama: sidebar + area kanan, tingginya selalu = viewport */}
      <div className="flex h-full min-w-0">
        {/* Sidebar admin (tidak ikut scroll karena page tidak scroll) */}
        <DashboardSidebarAdmin />

        {/* Area kanan */}
        <div className="flex-1 flex flex-col min-w-0">
          <DashboardNavbarAdmin displayName={displayName} />

          {/* MAIN yang scroll sendiri */}
          <main className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Header halaman */}
              <header className="bg-white rounded-2xl shadow-sm border border-slate-100 px-4 sm:px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-sky-500">
                    Affiliate Center
                  </p>
                  <h1 className="text-lg sm:text-xl font-semibold text-slate-900 mt-1">
                    Voucher & Komisi Affiliator
                  </h1>
                  <p className="text-xs sm:text-sm text-slate-500 mt-1">
                    Lihat daftar affiliator, voucher yang terhubung, dan
                    riwayat komisi / penarikan. Voucher tidak bisa diubah dari
                    halaman ini.
                  </p>
                </div>
                <div className="text-right text-[11px] sm:text-xs text-slate-500">
                  <div>Affiliator: {affiliators.length}</div>
                  {selectedHistoryAff && (
                    <div className="mt-1">
                      Fokus:{" "}
                      <span className="font-semibold text-slate-800">
                        {selectedHistoryAff.username ||
                          selectedHistoryAff.email}
                      </span>
                    </div>
                  )}
                </div>
              </header>

              {/* CARD 1: Affiliator + Wallet + Voucher (READ ONLY) */}
              <section className="bg-white rounded-2xl shadow-sm border border-slate-100 px-4 sm:px-5 py-4 space-y-4">
                {/* Title + search */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-sm sm:text-base font-semibold text-slate-900">
                      Daftar Affiliator & Voucher
                    </h2>
                    <p className="text-[11px] sm:text-xs text-slate-500 mt-1">
                      Voucher dan persentase komisi hanya ditampilkan (read
                      only). Jika perlu mengubah, lakukan langsung di dashboard
                      Supabase.
                    </p>
                  </div>
                  <div className="w-full sm:w-72">
                    <div className="relative">
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cari nama / email..."
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500"
                      />
                      <span className="pointer-events-none absolute right-3 top-1.5 text-slate-400 text-[13px]">
                        🔍
                      </span>
                    </div>
                  </div>
                </div>

                {/* Table-like list */}
                <div className="border border-slate-100 rounded-2xl bg-slate-50/60 overflow-hidden">
                  {/* Header tabel (desktop) */}
                  <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 text-[11px] font-semibold text-slate-500 border-b border-slate-100 bg-slate-50">
                    <div className="col-span-4">Affiliator</div>
                    <div className="col-span-2">Wallet</div>
                    <div className="col-span-3">Voucher</div>
                    <div className="col-span-3 text-right">Aksi</div>
                  </div>

                  {/* Rows */}
                  <div className="max-h-[55vh] overflow-y-auto px-2 pb-2 space-y-2">
                    {loadingAffiliators && (
                      <p className="text-xs text-slate-500 px-2 pt-2">
                        Mengambil data affiliator...
                      </p>
                    )}

                    {!loadingAffiliators && filteredAffiliators.length === 0 && (
                      <p className="text-xs text-slate-400 px-2 pt-2">
                        Tidak ada affiliator yang cocok.
                      </p>
                    )}

                    {!loadingAffiliators &&
                      filteredAffiliators.map((aff) => {
                        const v = vouchersMap[aff.id];
                        const isSelected =
                          selectedHistoryAff?.id === aff.id;

                        const pendingInfo = pendingMap[aff.id];

                        const rowBaseClass =
                          "flex flex-col md:grid md:grid-cols-12 gap-2 md:gap-3 px-3 py-3 border rounded-2xl transition-all bg-white relative";

                        const selectedClass = isSelected
                          ? "border-sky-300 bg-sky-50/80 shadow-sm"
                          : "border-slate-200 hover:bg-slate-50";

                        return (
                          <div
                            key={aff.id}
                            className={`${rowBaseClass} ${selectedClass}`}
                          >
                            {/* Aff info */}
                            <div className="md:col-span-4 flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                {(aff.username || aff.email || "?")
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs sm:text-sm font-semibold text-slate-900 truncate">
                                  {aff.username || aff.email}
                                </p>
                                <p className="text-[11px] text-slate-500 truncate">
                                  ID:{" "}
                                  <span className="font-mono text-[10px]">
                                    {aff.id}
                                  </span>
                                </p>
                              </div>
                            </div>

                            {/* Wallet */}
                            <div className="md:col-span-2 flex items-center">
                              <p className="text-xs text-slate-700">
                                <span className="text-[11px] text-slate-500 mr-1">
                                  Wallet:
                                </span>
                                <span className="font-semibold text-sky-700">
                                  {formatRupiah(aff.wallet_balance)}
                                </span>
                              </p>
                            </div>

                            {/* Voucher: READ ONLY */}
                            <div className="md:col-span-3">
                              <div className="space-y-0.5 text-[11px] sm:text-xs">
                                {v ? (
                                  <>
                                    <p className="font-mono text-sky-700">
                                      {v.code}
                                    </p>
                                    <p className="text-slate-600">
                                      Komisi default:{" "}
                                      <span className="font-semibold text-emerald-700">
                                        {v.commission_percent || 0}%
                                      </span>
                                      {v.is_active === false && (
                                        <span className="ml-1 text-[10px] text-rose-500">
                                          (Nonaktif)
                                        </span>
                                      )}
                                    </p>
                                  </>
                                ) : (
                                  <p className="italic text-slate-400">
                                    Belum ada voucher untuk user ini.
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Aksi: hanya RIWAYAT */}
                            <div className="md:col-span-3 flex flex-wrap justify-end gap-2 pt-1 items-center">
                              {pendingInfo && pendingInfo.count > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 bg-rose-500 text-white text-[10px] font-bold rounded-full shadow-sm animate-pulse mr-2">
                                  {pendingInfo.count}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => handleViewHistory(aff)}
                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-[11px] sm:text-xs text-slate-700 px-3 py-1.5 hover:bg-slate-50 relative"
                              >
                                Riwayat komisi & penarikan
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </section>

              {/* CARD 2: RIWAYAT KOMISI & PENARIKAN (hanya muncul kalau user diklik) */}
              {selectedHistoryAff && (
                <section className="bg-white rounded-2xl shadow-sm border border-slate-100 px-4 sm:px-5 py-4 space-y-4">
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-400">
                        Sumber Saldo
                      </p>
                      <h2 className="text-sm sm:text-base font-semibold text-slate-900 mt-1">
                        {selectedHistoryAff.username ||
                          selectedHistoryAff.email}
                      </h2>
                      <p className="text-[11px] sm:text-xs text-slate-500 mt-1">
                        Wallet sekarang:{" "}
                        <span className="font-semibold text-sky-700">
                          {formatRupiah(selectedHistoryAff.wallet_balance)}
                        </span>
                        {selectedHistoryVoucher && (
                          <>
                            {" · Voucher: "}
                            <span className="font-mono text-sky-700">
                              {selectedHistoryVoucher.code}
                            </span>
                            {" ("}
                            <span className="font-semibold text-emerald-700">
                              {selectedHistoryVoucher.commission_percent || 0}%
                            </span>
                            {")"}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="text-right text-[11px] sm:text-xs text-slate-500">
                      <p>
                        Total komisi tercatat:{" "}
                        <span className="font-semibold text-emerald-700">
                          {formatRupiah(totalCommission)}
                        </span>
                      </p>
                      <p>
                        Total penarikan tercatat:{" "}
                        <span className="font-semibold text-rose-700">
                          {formatRupiah(totalWithdrawal)}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* 2 kolom riwayat */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Riwayat komisi */}
                    <div className="flex flex-col min-h-[220px] max-h-[50vh] border border-slate-100 rounded-2xl bg-slate-50/60">
                      <div className="px-3 sm:px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                        <h3 className="text-xs font-semibold text-slate-800">
                          Riwayat Komisi
                        </h3>
                        {loadingDetails && (
                          <span className="text-[10px] text-slate-400">
                            Memuat...
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 py-2 space-y-2 text-[11px] sm:text-xs">
                        {loadingDetails && (
                          <p className="text-slate-500">
                            Mengambil data komisi...
                          </p>
                        )}

                        {!loadingDetails && commissions.length === 0 && (
                          <p className="text-slate-400">
                            Belum ada komisi untuk affiliator ini.
                          </p>
                        )}

                        {!loadingDetails &&
                          commissions.map((c) => (
                            <div
                              key={c.id}
                              className="bg-white rounded-lg border border-slate-200 px-2.5 py-2 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
                            >
                              <div className="flex justify-between gap-2">
                                <span className="font-semibold text-slate-800 truncate">
                                  {c.buyer_name || "Tanpa nama"}
                                </span>
                                <span className="text-[10px] text-slate-400 flex-shrink-0">
                                  {new Date(c.created_at).toLocaleString(
                                    "id-ID",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    }
                                  )}
                                </span>
                              </div>
                              <div className="mt-1 flex justify-between items-center gap-2">
                                <span className="text-slate-600">
                                  Voucher:{" "}
                                  <span className="font-mono text-[11px]">
                                    {c.voucher_code || "-"}
                                  </span>
                                </span>
                                <span className="uppercase text-[10px] text-slate-500">
                                  {c.status}
                                </span>
                              </div>
                              <div className="mt-1 flex justify-between text-slate-700">
                                <span>
                                  Transaksi:{" "}
                                  {formatRupiah(c.transaction_amount)}
                                </span>
                                <span className="font-semibold text-emerald-700">
                                  +{formatRupiah(c.commission_amount)}
                                </span>
                              </div>
                              <p className="mt-1 text-[10px] text-slate-500">
                                Diskon: {formatRupiah(c.discount_amount)}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Riwayat penarikan */}
                    <div className="flex flex-col min-h-[220px] max-h-[50vh] border border-slate-100 rounded-2xl bg-slate-50/60">
                      <div className="px-3 sm:px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                        <h3 className="text-xs font-semibold text-slate-800">
                          Riwayat Penarikan
                        </h3>
                        {pendingWithdrawalCount > 0 && (
                          <div className="text-[10px] sm:text-xs text-amber-700">
                            Pending:{" "}
                            <span className="font-semibold">
                              {pendingWithdrawalCount} transaksi
                            </span>
                            {" · "}
                            <span className="font-semibold">
                              {formatRupiah(pendingWithdrawalTotal)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 py-2 space-y-2 text-[11px] sm:text-xs">
                        {loadingDetails && withdrawals.length === 0 && (
                          <p className="text-slate-500">
                            Mengambil data penarikan...
                          </p>
                        )}

                        {!loadingDetails && withdrawals.length === 0 && (
                          <p className="text-slate-400">
                            Belum ada penarikan saldo.
                          </p>
                        )}

                        {!loadingDetails &&
                          withdrawals.map((w) => (
                            <div
                              key={w.id}
                              className="bg-white rounded-lg border border-slate-200 px-2.5 py-2 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
                            >
                              <div className="flex justify-between gap-2">
                                <span className="font-semibold text-slate-800">
                                  {formatRupiah(w.amount)}
                                </span>
                                <span className="text-[10px] text-slate-400 flex-shrink-0">
                                  {new Date(w.created_at).toLocaleString(
                                    "id-ID",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    }
                                  )}
                                </span>
                              </div>
                              <div className="mt-1 text-[10px] text-slate-500 flex justify-between gap-2">
                                <span>
                                  Status:{" "}
                                  <span className="uppercase tracking-wide">
                                    {w.status}
                                  </span>
                                </span>
                                {w.processed_at && (
                                  <span>
                                    Diproses:{" "}
                                    {new Date(
                                      w.processed_at
                                    ).toLocaleDateString("id-ID", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </span>
                                )}
                              </div>
                              {w.note && (
                                <p className="mt-1 text-[10px] text-slate-500">
                                  Catatan: {w.note}
                                </p>
                              )}

                              {/* SNAPSHOT BANK */}
                              {(w.bank_name || w.account_number) && (
                                <div className="mt-2 text-[10px] text-slate-600 bg-slate-50 p-1.5 rounded border border-slate-100">
                                  <p className="font-semibold text-slate-700">Info Transfer:</p>
                                  <p>{w.bank_name} - {w.account_number}</p>
                                  <p className="text-slate-500">a.n {w.account_holder}</p>
                                </div>
                              )}

                              {w.status === "pending" && (
                                <div className="mt-2">
                                  <textarea
                                    className="w-full text-[10px] sm:text-xs border border-slate-200 rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-sky-500 bg-slate-50"
                                    placeholder="Tulis catatan admin di sini (opsional)..."
                                    rows={2}
                                    value={
                                      withdrawalNotes[w.id] !== undefined
                                        ? withdrawalNotes[w.id]
                                        : w.note || ""
                                    }
                                    onChange={(e) =>
                                      setWithdrawalNotes((prev) => ({
                                        ...prev,
                                        [w.id]: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              )}

                              {w.status === "pending" && (
                                <div className="mt-2 flex flex-wrap justify-end gap-2">
                                  <button
                                    type="button"
                                    disabled={updatingWithdrawalId === w.id}
                                    onClick={() =>
                                      handleUpdateWithdrawalStatus(
                                        w,
                                        "success"
                                      )
                                    }
                                    className="inline-flex items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-[10px] sm:text-xs text-emerald-700 px-2.5 py-1 hover:bg-emerald-100 disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    Tandai berhasil
                                  </button>
                                  <button
                                    type="button"
                                    disabled={updatingWithdrawalId === w.id}
                                    onClick={() =>
                                      handleUpdateWithdrawalStatus(
                                        w,
                                        "failed"
                                      )
                                    }
                                    className="inline-flex items-center justify-center rounded-lg border border-rose-200 bg-rose-50 text-[10px] sm:text-xs text-rose-700 px-2.5 py-1 hover:bg-rose-100 disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    Batalkan / gagal
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminAffiliatePage;
